import * as XLSX from 'xlsx';

export const SUPPORTED_EXTENSIONS = ['xlsx', 'xls', 'csv'];

// Zero-width chars, word joiner and BOM: invisible in Excel but they break
// matching (typical of text pasted from chat apps). NBSP becomes a plain space.
const INVISIBLE_RE = /[\u200B-\u200D\u2060\uFEFF]/g;
const hasInvisible = (s) => /[\u200B-\u200D\u2060\uFEFF]/.test(s);
export const cleanText = (s) => String(s).replace(INVISIBLE_RE, '').replace(/\u00A0/g, ' ').trim();

/** Reads a File into a SheetJS workbook. Rejects with an Error whose message is a code. */
export const readWorkbook = (file) =>
	new Promise((resolve, reject) => {
		const extension = (file?.name || '').split('.').pop().toLowerCase();
		if (!SUPPORTED_EXTENSIONS.includes(extension)) {
			reject(new Error('UNSUPPORTED_FORMAT'));
			return;
		}
		const reader = new FileReader();
		reader.onerror = () => reject(new Error('FILE_READ_ERROR'));
		reader.onload = (event) => {
			try {
				// cellNF keeps each cell's number format so real date cells are
				// detected by format, not by however Excel happens to display them.
				const workbook = XLSX.read(new Uint8Array(event.target.result), { type: 'array', cellNF: true });
				resolve(workbook);
			} catch (error) {
				reject(new Error('FILE_PARSE_ERROR'));
			}
		};
		reader.readAsArrayBuffer(file);
	});

const pad = (n) => String(n).padStart(2, '0');

/**
 * One cell to a JSON-safe value: date cells -> 'YYYY-MM-DD', numeric cells ->
 * number (never the locale-formatted text), booleans -> boolean, text -> trimmed
 * string without invisible characters. Returns { value, invisible }.
 */
export const cellValue = (cell) => {
	if (!cell || cell.t === 'z' || cell.t === 'e') return { value: '', invisible: false };
	if (cell.t === 'n') {
		if (cell.z && XLSX.SSF.is_date(cell.z)) {
			const parsed = XLSX.SSF.parse_date_code(cell.v);
			if (parsed) return { value: `${parsed.y}-${pad(parsed.m)}-${pad(parsed.d)}`, invisible: false };
		}
		return { value: cell.v, invisible: false };
	}
	if (cell.t === 'd' && cell.v instanceof Date) {
		const d = cell.v;
		return { value: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, invisible: false };
	}
	if (cell.t === 'b') return { value: cell.v, invisible: false };
	const raw = String(cell.v ?? cell.w ?? '');
	return { value: cleanText(raw), invisible: hasInvisible(raw) };
};

const getRange = (sheet) => (sheet && sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']) : null);

/**
 * Sheet -> { headers, rows, invisible }.
 * The column names are always the first non-empty row of the sheet. Each
 * data row keeps its original Excel row number so every message points at the file.
 * Blank headers become "Columna X"; repeated headers get a " (2)" suffix.
 * invisible: Set of `${rowNumber}|${header}` for cells that had invisible chars.
 */
export const sheetToTable = (workbook, sheetName) => {
	const sheet = workbook?.Sheets?.[sheetName];
	const range = getRange(sheet);
	if (!range) return { headers: [], rows: [], invisible: new Set() };
	// The sheet's range may start with empty rows (it can be declared from A1)
	const rowIsEmpty = (r) => {
		for (let c = range.s.c; c <= range.e.c; c += 1) {
			if (cellValue(sheet[XLSX.utils.encode_cell({ r, c })]).value !== '') return false;
		}
		return true;
	};
	let headerIndex = range.s.r;
	while (headerIndex < range.e.r && rowIsEmpty(headerIndex)) headerIndex += 1;

	const headers = [];
	const seen = {};
	for (let c = range.s.c; c <= range.e.c; c += 1) {
		const { value } = cellValue(sheet[XLSX.utils.encode_cell({ r: headerIndex, c })]);
		let header = String(value || '').trim() || `Columna ${XLSX.utils.encode_col(c)}`;
		seen[header] = (seen[header] || 0) + 1;
		if (seen[header] > 1) header = `${header} (${seen[header]})`;
		headers.push(header);
	}

	const rows = [];
	const invisible = new Set();
	for (let r = headerIndex + 1; r <= range.e.r; r += 1) {
		const cells = {};
		let hasData = false;
		headers.forEach((header, i) => {
			const { value, invisible: hadInvisible } = cellValue(sheet[XLSX.utils.encode_cell({ r, c: range.s.c + i })]);
			cells[header] = value;
			if (value !== '' && value !== null && value !== undefined) hasData = true;
			if (hadInvisible) invisible.add(`${r + 1}|${header}`);
		});
		if (hasData) rows.push({ rowNumber: r + 1, cells });
	}
	return { headers, rows, invisible };
};

/**
 * Date typed as separate parts in the review -> 'YYYY-MM-DD', or '' while any
 * part is missing or out of range. Impossible days (31/11) are left to the
 * server, which reports them on the row.
 */
export const partsToIso = ({ day, month, year } = {}) => {
	const d = Number(day);
	const m = Number(month);
	const y = Number(year);
	if (!Number.isInteger(d) || d < 1 || d > 31) return '';
	if (!Number.isInteger(m) || m < 1 || m > 12) return '';
	if (!Number.isInteger(y) || y < 1900 || y > 2100) return '';
	return `${y}-${pad(m)}-${pad(d)}`;
};

/** Failed rows + a reason column, downloaded as .xlsx. */
export const downloadErrorReport = (headers, rows, fileName) => {
	const data = rows.map(({ rowNumber, cells, error }) => {
		const record = { Fila: rowNumber };
		headers.forEach((header) => {
			record[header] = cells[header];
		});
		record.Error = error;
		return record;
	});
	const sheet = XLSX.utils.json_to_sheet(data, { header: ['Fila', ...headers, 'Error'] });
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, sheet, 'Errores');
	XLSX.writeFile(workbook, fileName);
};
